import React, { Suspense } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './config/queryClient';
import { AuthProvider } from './contexts/AuthContext';
import CoachRoute from './components/auth/CoachRoute';
import AdminRoute from './components/auth/AdminRoute';
import ProtectedRoute from './components/auth/ProtectedRoute';
import RowerRoute from './components/auth/RowerRoute';
import ParentRoute from './components/auth/ParentRoute';
import DevRoute from './components/auth/DevRoute';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import { useAuth } from './contexts/AuthContext';
import { useEffect } from 'react';
import { profileService } from './services/users/profileService';
import { UserRole } from './types/database';
// import { EnvironmentBanner } from './components/layout/EnvironmentBanner';
// import TestSentry from './components/TestSentry';
import CookieConsentBanner from './components/common/CookieConsentBanner';
import AppLayout from './components/layout/AppLayout';
import { isPrototypesEnabled } from './config/debug';
import {
  isRowerPeachIntegrationEnabled,
  isRowerSharingFeaturesEnabled,
} from './config/featureFlags';
import { captureCampaignAttributionFromLocation } from './utils/campaignAttribution';

// Layout components

// Lazy load non-critical pages for better performance
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const PricingPage = React.lazy(() => import('./pages/PricingPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));

// Auth pages - Lazy load since they're not immediately needed
const LoginPage = React.lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/auth/RegisterPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/auth/ResetPasswordPage'));
const MfaEnrollPage = React.lazy(() => import('./pages/auth/MfaEnrollPage'));
const MfaVerifyPage = React.lazy(() => import('./pages/auth/MfaVerifyPage'));
const AcceptInvitationPage = React.lazy(() => import('./pages/rower/AcceptInvitationPage'));
const MinorInviteConsentPage = React.lazy(() => import('./pages/rower/MinorInviteConsentPage'));
const RowerCompleteSignupPage = React.lazy(() => import('./pages/rower/RowerCompleteSignupPage'));
const ParentAcceptConsentPage = React.lazy(() => import('./pages/parent/ParentAcceptConsentPage'));
const ParentReconsentPage = React.lazy(() => import('./pages/parent/ParentReconsentPage'));
const RowerInvitationsPage = React.lazy(() => import('./pages/rower/RowerInvitationsPage'));
const RowerDashboard = React.lazy(() => import('./pages/rower/RowerDashboard'));
const RowerSettingsPage = React.lazy(() => import('./pages/rower/RowerSettingsPage'));
const RowerIntegrationsPage = React.lazy(() => import('./pages/rower/RowerIntegrationsPage'));
const RowerConcept2IntegrationPage = React.lazy(
  () => import('./pages/rower/RowerConcept2IntegrationPage')
);
const RowerPeachIntegrationPage = React.lazy(
  () => import('./pages/rower/RowerPeachIntegrationPage')
);
const RowerSessionsPage = React.lazy(() => import('./pages/rower/RowerSessionsPage'));
const RowerSessionDetailPage = React.lazy(() => import('./pages/rower/RowerSessionDetailPage'));
const RowerTeamsPage = React.lazy(() => import('./pages/rower/RowerTeamsPage'));
const RowerTeamDetailPage = React.lazy(() => import('./pages/rower/RowerTeamDetailPage'));
const RowerRecruitmentPage = React.lazy(() => import('./pages/rower/RowerRecruitmentPage'));
const RowerSchoolBrowsePage = React.lazy(() => import('./pages/rower/RowerSchoolBrowsePage'));
const RowerRequestParentConsentPage = React.lazy(
  () => import('./pages/rower/RowerRequestParentConsentPage')
);
const RowerNotificationsPage = React.lazy(() => import('./pages/rower/RowerNotificationsPage'));
const RowerInboxPage = React.lazy(() => import('./pages/rower/RowerInboxPage'));
const CoachNotificationsPage = React.lazy(() => import('./pages/coach/CoachNotificationsPage'));
const CoachInboxPage = React.lazy(() => import('./pages/coach/CoachInboxPage'));
const MyDataAccessPage = React.lazy(() => import('./pages/athlete/MyDataAccessPage'));
const ParentPortal = React.lazy(() => import('./pages/parent/ParentPortal'));
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const AdminNotificationsPage = React.lazy(() => import('./pages/admin/AdminNotificationsPage'));
const AdminInboxPage = React.lazy(() => import('./pages/admin/AdminInboxPage'));
const AdminSettingsPage = React.lazy(() => import('./pages/admin/AdminSettingsPage'));
const AdminOrganizationsPage = React.lazy(() => import('./pages/admin/AdminOrganizationsPage'));
const AdminRecruitmentCatalogPage = React.lazy(
  () => import('./pages/admin/AdminRecruitmentCatalogPage')
);
const AdminRecruitmentProgramDetailPage = React.lazy(
  () => import('./pages/admin/AdminRecruitmentProgramDetailPage')
);
const AdminOrganizationDetailPage = React.lazy(
  () => import('./pages/admin/AdminOrganizationDetailPage')
);
const AdminActivityAuditPage = React.lazy(() => import('./pages/admin/AdminActivityAuditPage'));
const AdminConsentsPage = React.lazy(() => import('./pages/admin/AdminConsentsPage'));
const AdminDataRightsPage = React.lazy(() => import('./pages/admin/AdminDataRightsPage'));
const ComplianceDashboardPage = React.lazy(() => import('./pages/admin/ComplianceDashboardPage'));
const SystemDataMapPage = React.lazy(() => import('./pages/admin/SystemDataMapPage'));
const OrgHierarchyPage = React.lazy(() => import('./pages/admin/OrgHierarchyPage'));
const UserFlowsPage = React.lazy(() => import('./pages/admin/UserFlowsPage'));
const CoachUserFlowPage = React.lazy(() => import('./pages/admin/CoachUserFlowPage'));
const RowerUserFlowPage = React.lazy(() => import('./pages/admin/RowerUserFlowPage'));
const ParentUserFlowPage = React.lazy(() => import('./pages/admin/ParentUserFlowPage'));

// Role-aware home redirect for authenticated users hitting "/home"
const RoleAwareHome: React.FC = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const go = async () => {
      if (!user) {
        navigate('/', { replace: true });
        return;
      }
      const profile = await profileService.getProfile(user.id);
      if (profile?.user_role === UserRole.Coach) {
        navigate('/coach', { replace: true });
        return;
      }
      if (profile?.user_role === UserRole.Admin || profile?.user_role === UserRole.Superadmin) {
        navigate('/admin', { replace: true });
        return;
      }
      if (profile?.user_role === UserRole.Athlete) {
        navigate('/rower', { replace: true });
        return;
      }
      if (profile?.user_role === UserRole.Parent) {
        navigate('/parent', { replace: true });
        return;
      }
      // Avoid forcing coach route as a fallback; send unknown roles to login.
      navigate('/login', { replace: true });
    };
    if (!loading) {
      go();
    }
  }, [user, loading, navigate]);

  return null;
};

// Redirect component for /accept-invitation to /rower/invitations
const RedirectToRowerInvitations: React.FC = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const location = useLocation();
  return <Navigate to={`/rower/invitations${location.search}`} replace />;
};

// Auth-aware landing for "/": if logged in, send to /home; otherwise show public landing
const AuthAwareLanding: React.FC = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/home" replace />;
  return <Navigate to="/login" replace />;
};

// Legal pages
const PrivacyPolicyPage = React.lazy(() => import('./pages/legal/PrivacyPolicyPage'));
const TermsOfServicePage = React.lazy(() => import('./pages/legal/TermsOfServicePage'));
const CookiePolicyPage = React.lazy(() => import('./pages/legal/CookiePolicyPage'));
const CoppaRightsPage = React.lazy(() => import('./pages/legal/CoppaRightsPage'));
const DataSubjectRequestPage = React.lazy(() => import('./pages/legal/DataSubjectRequestPage'));

// Coach pages - Lazy load heavy chart components
const CoachPortal = React.lazy(() => import('./pages/coach/CoachPortal'));
const PrototypesRoot = React.lazy(() => import('./pages/coach/PrototypesRoot'));
const RowerPrototypesRoot = React.lazy(() => import('./pages/rower/RowerPrototypesRoot'));
// const GraphSDKTestPage = React.lazy(() => import('./pages/coach/GraphSDKTestPage')); // Graph platform development complete

// Loading component for lazy-loaded routes - matches existing design system
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
      <p className="text-gray-600 text-sm">Loading...</p>
    </div>
  </div>
);

function App() {
  useEffect(() => {
    captureCampaignAttributionFromLocation();
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router>
          <AuthProvider>
            <AppLayout>
              {/* <EnvironmentBanner /> */}
              {/* {process.env.NODE_ENV === 'development' && <TestSentry />} */}
              <CookieConsentBanner />

              <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  {/* Public landing page (auth-aware) */}
                  <Route path="/" element={<AuthAwareLanding />} />

                  {/* Auth home redirect: if logged in, route by role */}
                  <Route path="/home" element={<RoleAwareHome />} />

                  {/* Public routes */}
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/contact" element={<ContactPage />} />

                  {/* Legal pages */}
                  <Route path="/privacy" element={<PrivacyPolicyPage />} />
                  <Route
                    path="/privacy/data-subject-request"
                    element={<DataSubjectRequestPage />}
                  />
                  <Route path="/terms" element={<TermsOfServicePage />} />
                  <Route path="/cookies" element={<CookiePolicyPage />} />
                  <Route path="/coppa-rights" element={<CoppaRightsPage />} />

                  {/* Auth routes */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route
                    path="/mfa/enroll"
                    element={
                      <ProtectedRoute>
                        <MfaEnrollPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/mfa/verify"
                    element={
                      <ProtectedRoute>
                        <MfaVerifyPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Legacy landing path keeps users in app auth flow */}
                  <Route path="/coach-landing" element={<Navigate to="/login" replace />} />

                  {/* Coach routes - enforce coach authorization via CoachRoute */}
                  <Route
                    path="/coach/*"
                    element={
                      <CoachRoute>
                        <CoachPortal />
                      </CoachRoute>
                    }
                  />
                  <Route
                    path="/coach/notifications"
                    element={
                      <CoachRoute>
                        <CoachNotificationsPage />
                      </CoachRoute>
                    }
                  />
                  <Route
                    path="/coach/inbox"
                    element={
                      <CoachRoute>
                        <CoachInboxPage />
                      </CoachRoute>
                    }
                  />
                  {/* Profile is now part of Settings at /coach/settings */}
                  <Route
                    path="/coach/profile"
                    element={
                      <CoachRoute>
                        <Navigate to="/coach/settings" replace />
                      </CoachRoute>
                    }
                  />
                  {isPrototypesEnabled() && (
                    <Route
                      path="/coach/prototypes/*"
                      element={
                        <CoachRoute>
                          <DevRoute fallbackPath="/coach">
                            <PrototypesRoot />
                          </DevRoute>
                        </CoachRoute>
                      }
                    />
                  )}
                  {/* Rower routes */}
                  <Route path="/rower/invitations/minor" element={<MinorInviteConsentPage />} />
                  <Route path="/rower/complete-signup" element={<RowerCompleteSignupPage />} />
                  <Route
                    path="/rower/accept-invitation"
                    element={
                      <ProtectedRoute>
                        <RowerRoute>
                          <AcceptInvitationPage />
                        </RowerRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/accept-invitation"
                    element={
                      <ProtectedRoute>
                        <RedirectToRowerInvitations />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/rower/invitations"
                    element={
                      <ProtectedRoute>
                        <RowerRoute>
                          <RowerInvitationsPage />
                        </RowerRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/rower"
                    element={
                      <ProtectedRoute>
                        <RowerRoute>
                          <RowerDashboard />
                        </RowerRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/rower/settings"
                    element={
                      <ProtectedRoute>
                        <RowerRoute>
                          <RowerSettingsPage />
                        </RowerRoute>
                      </ProtectedRoute>
                    }
                  />
                  {isRowerSharingFeaturesEnabled() ? (
                    <>
                      <Route
                        path="/rower/integrations"
                        element={
                          <ProtectedRoute>
                            <RowerRoute>
                              <RowerIntegrationsPage />
                            </RowerRoute>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/rower/integrations/concept2"
                        element={
                          <ProtectedRoute>
                            <RowerRoute>
                              <RowerConcept2IntegrationPage />
                            </RowerRoute>
                          </ProtectedRoute>
                        }
                      />
                      {isRowerPeachIntegrationEnabled() && (
                        <Route
                          path="/rower/integrations/peach"
                          element={
                            <ProtectedRoute>
                              <RowerRoute>
                                <RowerPeachIntegrationPage />
                              </RowerRoute>
                            </ProtectedRoute>
                          }
                        />
                      )}
                    </>
                  ) : (
                    <>
                      <Route
                        path="/rower/integrations"
                        element={<Navigate to="/rower" replace />}
                      />
                      <Route
                        path="/rower/integrations/*"
                        element={<Navigate to="/rower" replace />}
                      />
                    </>
                  )}
                  {isPrototypesEnabled() && (
                    <Route
                      path="/rower/prototypes/*"
                      element={
                        <ProtectedRoute>
                          <RowerRoute>
                            <DevRoute fallbackPath="/rower">
                              <RowerPrototypesRoot />
                            </DevRoute>
                          </RowerRoute>
                        </ProtectedRoute>
                      }
                    />
                  )}
                  {isRowerPeachIntegrationEnabled() ? (
                    <>
                      <Route
                        path="/rower/sessions"
                        element={
                          <ProtectedRoute>
                            <RowerRoute>
                              <RowerSessionsPage />
                            </RowerRoute>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/rower/sessions/:sessionId"
                        element={
                          <ProtectedRoute>
                            <RowerRoute>
                              <RowerSessionDetailPage />
                            </RowerRoute>
                          </ProtectedRoute>
                        }
                      />
                    </>
                  ) : (
                    <>
                      <Route
                        path="/rower/sessions"
                        element={
                          <Navigate
                            to={
                              isRowerSharingFeaturesEnabled()
                                ? '/rower/integrations/concept2'
                                : '/rower'
                            }
                            replace
                          />
                        }
                      />
                      <Route
                        path="/rower/sessions/:sessionId"
                        element={
                          <Navigate
                            to={
                              isRowerSharingFeaturesEnabled()
                                ? '/rower/integrations/concept2'
                                : '/rower'
                            }
                            replace
                          />
                        }
                      />
                      {isRowerSharingFeaturesEnabled() && (
                        <Route
                          path="/rower/integrations/peach"
                          element={<Navigate to="/rower/integrations" replace />}
                        />
                      )}
                    </>
                  )}
                  {isRowerSharingFeaturesEnabled() ? (
                    <>
                      <Route
                        path="/rower/teams"
                        element={
                          <ProtectedRoute>
                            <RowerRoute>
                              <RowerTeamsPage />
                            </RowerRoute>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/rower/teams/:teamId"
                        element={
                          <ProtectedRoute>
                            <RowerRoute>
                              <RowerTeamDetailPage />
                            </RowerRoute>
                          </ProtectedRoute>
                        }
                      />
                    </>
                  ) : (
                    <>
                      <Route path="/rower/teams" element={<Navigate to="/rower" replace />} />
                      <Route path="/rower/teams/*" element={<Navigate to="/rower" replace />} />
                    </>
                  )}
                  <Route
                    path="/rower/recruitment"
                    element={
                      <ProtectedRoute>
                        <RowerRoute>
                          <RowerRecruitmentPage />
                        </RowerRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/rower/recruitment/browse"
                    element={
                      <ProtectedRoute>
                        <RowerRoute>
                          <RowerSchoolBrowsePage />
                        </RowerRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/rower/my-data-access"
                    element={
                      <ProtectedRoute>
                        <RowerRoute>
                          <MyDataAccessPage />
                        </RowerRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/rower/request-parent-consent"
                    element={
                      <ProtectedRoute>
                        <RowerRoute>
                          <RowerRequestParentConsentPage />
                        </RowerRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/rower/notifications"
                    element={
                      <ProtectedRoute>
                        <RowerRoute>
                          <RowerNotificationsPage />
                        </RowerRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/rower/inbox"
                    element={
                      <ProtectedRoute>
                        <RowerRoute>
                          <RowerInboxPage />
                        </RowerRoute>
                      </ProtectedRoute>
                    }
                  />
                  {/* Parent routes */}
                  <Route path="/parent/consent" element={<ParentAcceptConsentPage />} />
                  <Route path="/parent/reconsent" element={<ParentReconsentPage />} />
                  {isPrototypesEnabled() && (
                    <Route path="/parent/preview/*" element={<ParentPortal preview />} />
                  )}
                  <Route
                    path="/parent/*"
                    element={
                      <ProtectedRoute>
                        <ParentRoute>
                          <ParentPortal />
                        </ParentRoute>
                      </ProtectedRoute>
                    }
                  />
                  {/* Admin routes */}
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <AdminDashboard />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/notifications"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <AdminNotificationsPage />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/inbox"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <AdminInboxPage />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/settings"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <AdminSettingsPage />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/organizations"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <AdminOrganizationsPage />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/organizations/:organizationId"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <AdminOrganizationDetailPage />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/recruitment-catalog"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <AdminRecruitmentCatalogPage />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/recruitment-catalog/:schoolId"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <AdminRecruitmentProgramDetailPage />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/activity"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <AdminActivityAuditPage />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/compliance"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <ComplianceDashboardPage />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/consents"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <AdminConsentsPage />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/data-rights"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <AdminDataRightsPage />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/system-map"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <SystemDataMapPage />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/org-hierarchy"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <OrgHierarchyPage />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/user-flows"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <UserFlowsPage />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/user-flows/coach"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <CoachUserFlowPage />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/user-flows/rower"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <RowerUserFlowPage />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/user-flows/parent"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <ParentUserFlowPage />
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  {/* Graph Platform Test Page - Development Complete */}
                  {/* <Route
                    path="/coach/graph-sdk-test"
                    element={
                      <CoachRoute>
                        <GraphSDKTestPage />
                      </CoachRoute>
                    }
                  /> */}

                  {/* Catch-all route - redirect to home */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </AppLayout>
          </AuthProvider>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
